import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';

import SVGIcon from 'components/svg-icon/SVGIcon';
import CloudinaryImage from 'components/CloudinaryImage';
import Tooltip from 'components/Tooltip';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { getCloudinaryVideoThumbnail } from 'utils/cloudinary';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  getYoutubeVideoThumbnail
} from 'utils/channels';
import { isInstagramUrl, isS3Url, isTikTokVideoUrl, isValidAssetUrl, isYoutubeUrl } from 'utils/url';
import { getTikTokThumbnailUrlFromVideoUrl } from 'utils/tiktok-thumbnail';
import { fetchInstagramThumbnailUrl } from 'utils/content/instagram-gql-fallback';
import { __t, applyTranslations } from 'utils/language';
import { LOADING_PLACEHOLDER_IMAGE } from 'utils/image';

import type { MediaPreviewAttachment } from 'types';
import type { ConsentReducerConsentsMap, ConsentReducerSupportedChannels } from 'redux-store/types/ConsentReducer.type';
import type { CloudinaryVideoMapping } from 'redux-store/types/CloudinaryVideosReducer.type';

applyTranslations(require('./media-preview.i18n.json'));

type OwnProps = {
  attachment: MediaPreviewAttachment,
  width: number,
  height: number,
  className?: string | null,
  showPdfFileName?: boolean,
  useAssetDirectly?: boolean
};

type StateProps = {
  userChannelConsents: ConsentReducerConsentsMap,
  videoMapping: Record<string, CloudinaryVideoMapping>
};

type Props = OwnProps & StateProps;

type State = {
  thumbnailUrl: string
};

class MediaPreview extends React.PureComponent<Props, State> {
  static defaultProps = {
    showPdfFileName: true
  };

  state = {
    thumbnailUrl: ''
  };

  componentDidMount() {
    const { attachment, userChannelConsents } = this.props;
    const isTikTok = isTikTokVideoUrl(attachment.url);
    const isInstagram = isInstagramUrl(attachment.url);

    if (isTikTok && userChannelConsents[CHANNEL_TYPE_TIKTOK]) {
      getTikTokThumbnailUrlFromVideoUrl(attachment.url).then(thumbnailUrl => {
        this.setState({ thumbnailUrl });
      }).catch(() => {
        this.setState({ thumbnailUrl: '' });
      });
    }

    if (isInstagram && userChannelConsents[CHANNEL_TYPE_INSTAGRAM]) {
      fetchInstagramThumbnailUrl(attachment.url).then(thumbnailUrl => {
        this.setState({ thumbnailUrl });
      }).catch(() => {
        this.setState({ thumbnailUrl: '' });
      });
    }
  }

  private getType(): ConsentReducerSupportedChannels | 'other' {
    const { attachment } = this.props;
    if (isYoutubeUrl(attachment.url)) {
      return CHANNEL_TYPE_YOUTUBE;
    }

    if (isTikTokVideoUrl(attachment.url)) {
      return CHANNEL_TYPE_TIKTOK;
    }

    if (isInstagramUrl(attachment.url)) {
      return CHANNEL_TYPE_INSTAGRAM;
    }

    return 'other';
  }

  private hasConsentForCurrentChannel() {
    const { userChannelConsents } = this.props;
    const type = this.getType();

    return type === 'other' || userChannelConsents[type];
  }

  getThumbnailUrl = (): string => {
    const { attachment, videoMapping, useAssetDirectly } = this.props;
    const { thumbnailUrl } = this.state;

    const isYoutube = isYoutubeUrl(attachment.url);

    if (thumbnailUrl) {
      return thumbnailUrl;
    }

    if (isYoutube) {
      return getYoutubeVideoThumbnail(attachment.url);
    }

    if (attachment.format === 'video') {
      if (isS3Url(attachment.url)) {
        if (videoMapping[attachment.url]) {
          return getCloudinaryVideoThumbnail(videoMapping[attachment.url].cloudinaryUrl);
        }

        if (useAssetDirectly) {
          return getCloudinaryVideoThumbnail(attachment.url);
        }

        return LOADING_PLACEHOLDER_IMAGE;
      }

      return getCloudinaryVideoThumbnail(attachment.url);
    }

    return attachment.url;
  };

  private renderVideoExplanation(): React.ReactNode {
    const { attachment, videoMapping, useAssetDirectly } = this.props;
    const shouldRenderTooltip = attachment.format === 'video'
      && isS3Url(attachment.url)
      && !videoMapping[attachment.url];

    if (!shouldRenderTooltip || useAssetDirectly) {
      return null;
    }

    return (
      <div className="media-preview__tooltip">
        <Tooltip
          message={__t('media_preview_component.thumbnail_in_progress')}
          width="auto-compact"
        >
          <SVGDynamicIcon
            name="question-mark-inside-circle-custom-bg"
            width={16}
            height={16}
            customColor="grey"
          />
        </Tooltip>
      </div>
    );
  }

  render(): React.ReactNode {
    const { attachment, width, height, className, showPdfFileName } = this.props;

    const mediaClasses = classnames({
      'media-preview': true,
      [`media-preview--${String(attachment.format)}`]: attachment.format,
      [String(className)]: className
    });

    if (!isValidAssetUrl(attachment.url)) {
      return (
        <div className={mediaClasses}>
          <a
            href={attachment.url}
            rel="noopener noreferrer"
            target="_blank"
            className="media-preview__preview media-preview__preview--text-only"
            style={{
              width: `${width}px`,
              height: `${height}px`
            }}
            data-hj-suppress
          >
            {__t('media_preview_component.open_link')}
          </a>
        </div>
      );
    }

    const attachmentUrl = attachment.externalUrl || attachment.url;

    const type = this.getType();

    if (!this.hasConsentForCurrentChannel() && type !== 'other') {
      return (
        <div className={mediaClasses}>
          <div
            className="media-preview__preview"
            style={{
              width: `${width}px`,
              height: `${height}px`
            }}
          >
            <ConsentPlaceholder
              withLearnMore
              type={type}
              link={attachmentUrl}
            />
          </div>
        </div>
      );
    }

    const url = this.getThumbnailUrl();

    return (
      <a
        className={mediaClasses}
        rel="noopener noreferrer"
        target="_blank"
        href={attachmentUrl}
      >
        {
          ((attachment.externalUrl || attachment.format === 'video') && attachment.format !== 'pdf') ? (
            <div
              className="media-preview__preview"
              style={{
                width: `${width}px`,
                height: `${height}px`,
                backgroundImage: `url(${url})`
              }}
            />
          ) : (
            <div className="media-preview__image-pdf">
              <CloudinaryImage
                image={{ url }}
                width={width}
                height={height}
                alt={attachment.fileName ?? ''}
              />
              {
                  attachment.format === 'pdf' && showPdfFileName && (
                    <Tooltip
                      message={attachment.fileName || ''}
                      overflowWrap
                    >
                      <div className="media-preview__file-name" style={{ maxWidth: `${width}px` }}>
                        {attachment.fileName}
                      </div>
                    </Tooltip>
                  )
                }
            </div>
          )
        }
        {
          attachment.format === 'video' && (
            <div className="media-preview__play">
              <SVGIcon
                name="play-button"
                width={40}
                height={40}
              />
            </div>
          )
        }
        {this.renderVideoExplanation()}
      </a>
    );
  }
}

const mapStateToProps = ({ consentReducer, cloudinaryVideos }): StateProps => ({
  userChannelConsents: consentReducer.consents,
  videoMapping: cloudinaryVideos.videoMapping
});

export default connect(mapStateToProps)(MediaPreview);
